// src/components/ShareButton.jsx

import { useState } from 'react';
import { FaWhatsapp, FaFacebook, FaTwitter, FaEnvelope, FaShare, FaRegCopy } from 'react-icons/fa';
import notify from '../lib/notify';
import { Button as FcButton, Card as FcCard } from '../ui';
import { Dialog as FcDialog } from '../ui';

/**
 * ShareButton Component
 * 
 * Provides a unified sharing experience across the platform:
 * - Mobile: Native Web Share API (includes SMS, AirDrop, all installed apps)
 * - Desktop/Fallback: Modal with social sharing buttons and copy link
 * 
 * @param {string} url - The URL to share
 * @param {string} title - Title for the share (e.g., "Join my competition")
 * @param {string} text - Additional text/description for the share
 * @param {string} type - Type of share: 'competition', 'referral', 'achievement' (optional)
 * @param {string} className - Additional CSS classes (optional)
 * @param {React.ReactNode} children - Button content (optional, defaults to share icon)
 */
export default function ShareButton({ 
  url, 
  title, 
  text = '', 
  type = 'general',
  className = '',
  children 
}) {
  const [showModal, setShowModal] = useState(false);

  // Check if Web Share API is available
  const canUseNativeShare = typeof navigator !== 'undefined' && navigator.share;

  const handleShare = async () => {
    // Try native share first
    if (canUseNativeShare) {
      try {
        await navigator.share({
          title,
          text,
          url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          setShowModal(true);
        }
      }
    } else {
      // No native share support, show modal
      setShowModal(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      notify('Link copied to clipboard! ðŸ“‹', 'success');
      setShowModal(false);
    } catch (error) {
      notify('Failed to copy link', 'error');
    }
  };

  const handleSocialShare = (platform) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedText = encodeURIComponent(text);
    let shareUrl = '';

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedTitle}%0A%0A${encodedText}%0A%0A${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowModal(false);
  };

  return (
    <>
      {/* Share Button */}
      <FcButton variant="secondary" size="compact" type="submit"
        onClick={handleShare}
        className={className || "flex items-center gap-2"}
      >
        {children || (
          <>
            <FaShare className="text-sm" />
            Share
          </>
        )}
      </FcButton>

      {/* Fallback Modal for Desktop/Unsupported Browsers */}
      {showModal && (
        <FcDialog open title="Share" size="standard" onCancel={() => setShowModal(false)}>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-xl max-w-md w-full p-6">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4">

              <FcButton variant="tertiary" type="submit"
                onClick={() => setShowModal(false)}
                className=""
              >
                Ã—
              </FcButton>
            </div>

            {/* Title and URL Preview */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-ui-green mb-2">{title}</p>
              {text && <p className="text-sm text-content-secondary mb-3">{text}</p>}
              <div className="bg-surface-canvas p-3 rounded-lg">
                <p className="text-xs text-content-secondary break-all">{url}</p>
              </div>
            </div>

            {/* Copy Link Button */}
            <FcButton variant="tertiary" type="submit"
              onClick={handleCopyLink}
              className="w-full flex items-center justify-center gap-2 mb-4"
            >
              <FaRegCopy />
              Copy Link
            </FcButton>

            {/* Social Share Buttons */}
            <div className="space-y-2">
              <p className="text-sm text-content-secondary mb-3">Or share via:</p>
              
              <FcButton variant="tertiary" type="submit"
                onClick={() => handleSocialShare('whatsapp')}
                className="w-full flex items-center justify-center gap-2"
              >
                <FaWhatsapp className="text-xl" />
                WhatsApp
              </FcButton>

              <FcButton variant="tertiary" type="submit"
                onClick={() => handleSocialShare('facebook')}
                className="w-full flex items-center justify-center gap-2"
              >
                <FaFacebook className="text-xl" />
                Facebook
              </FcButton>

              <FcButton variant="tertiary" type="submit"
                onClick={() => handleSocialShare('twitter')}
                className="w-full flex items-center justify-center gap-2"
              >
                <FaTwitter className="text-xl" />
                Twitter
              </FcButton>

              <FcButton variant="tertiary" type="submit"
                onClick={() => handleSocialShare('email')}
                className="w-full flex items-center justify-center gap-2"
              >
                <FaEnvelope className="text-xl" />
                Email
              </FcButton>
            </div>
          </FcCard>
        </FcDialog>
      )}
    </>
  );
}
